/// <reference types="vite/client" />

import axios from 'axios'
import { useAuthStore } from '../stores/auth'

// Use Vite's built-in type reference instead of custom declarations
const API_URL = import.meta.env.VITE_API_URL || ''

export const client = axios.create({
  baseURL: API_URL,
})

// Add auth token to requests
client.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token
  if (token) {
    config.headers.Authorization = `Token ${token}`
  }
  return config
})

// Handle auth errors
client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      useAuthStore.getState().logout()
    }
    return Promise.reject(error)
  }
)

export interface ArrivalTimeSlot {
  id: number
  start_time: string
  end_time: string
  is_full: boolean
}

export interface Event {
  id: number
  name: string
  description: string
  start_date: string
  end_date: string
  is_full: boolean
  arrival_timeslots?: ArrivalTimeSlot[]
}

export interface Child {
  id: number
  first_name: string
  last_name: string
  gender: string
  birth_date: string
  registration_number: string
  social_security_number: string
  family_status: string
  parent_type: string
}

export interface MedicalHistory {
  id: number
  child: number
  year: number
  weight: number
  blood_type: string
  
  // Vaccination information
  is_vaccinated: boolean
  missing_vaccines: number[]
  other_missing_vaccines: string
  
  // Known diseases
  has_any_known_disease: boolean
  known_diseases: number[]
  other_known_diseases: string
  
  // Allergies
  has_allergies: boolean
  allergies: number[]
  other_allergies: string
  
  // Major disease
  has_any_major_disease: boolean
  major_disease: string
  
  // Psychological disorders
  has_psychological_disorder: boolean
  psychological_disorders: number[]
  other_psychological_disorders: string
  
  // Medication
  is_receiving_medication: boolean
  medication: string
  
  // Development disorders
  has_any_development_disorder: boolean
  development_disorders: number[]
  other_development_disorders: string
  
  // Specialist
  is_being_watched_by_specialist: boolean
  specialist_contact_info: string
  
  // Health issues
  has_worth_mentioning_health_issues: boolean
  worth_mentioning_health_issues: string
  
  // Risk group
  belongs_to_risk_group: boolean
  risk_group_explanation: string
  
  // Underlying diseases
  has_underlying_disease: boolean
  underlying_diseases: number[]
  other_underlying_diseases: string
  
  created_at?: string
  updated_at?: string
}

export interface MedicalData {
  blood_types: string[]
  missing_vaccines: Array<{ id: number, name: string }>
  known_diseases: Array<{ id: number, name: string }>
  allergies: Array<{ id: number, name: string }>
  psychological_disorders: Array<{ id: number, name: string }>
  development_disorders: Array<{ id: number, name: string }>
  underlying_diseases: Array<{ id: number, name: string }>
}

export interface Booking {
  id: number
  child: number
  event: number
  event_name?: string
  status: 'interested' | 'awaiting-communication' | 'info-verified' | 'confirmed' | 'cancelled' | 'completed' | 'detail_checking' | 'finalized'
  code: string
  friends?: number[]
  masked_friends?: string[]
  friend_registration_numbers?: string[]
  comments?: string
  social_security_fund_id?: number
  social_security_sub_fund_id?: number
  social_security_fund?: SocialSecurityFund | null
  social_security_sub_fund?: SocialSecuritySubFund | null
  dypa_protocol_number?: string
  efka_voucher_number?: string
  arrival_timeslot?: ArrivalTimeSlot | null
  arrival_timeslot_id?: number | null
  in_waiting_list?: boolean
  is_payment_eligible?: boolean
  has_advance_payment?: boolean
  available_payment_types?: PaymentType[]
  payments_info?: Array<{
    id: number
    merchant_reference: string
    payment_type: PaymentType
    is_early_bird?: boolean
    amount: string
    status: PaymentStatus
  }>
  created_at: string
  updated_at: string
}

export interface ParentChildRelationship {
  id: number
  parent: number
  child: number
  parent_type: string
}

export interface SocialSecurityFund {
  id: number
  name: string
  description: string
  requires_payment_in_advance: boolean
  sub_funds: SocialSecuritySubFund[]
}

export interface SocialSecuritySubFund {
  id: number
  name: string
  description: string
  requires_payment_in_advance: boolean
}

export const fetchUpcomingEvents = async (): Promise<Event[]> => {
  const response = await client.get('/events/upcoming/')
  return response.data
}

export const fetchChildren = async (): Promise<Child[]> => {
  const response = await client.get('/children/')
  return response.data
}

export const fetchSocialSecurityFunds = async (): Promise<SocialSecurityFund[]> => {
  const response = await client.get('/social-security-funds/')
  return response.data
}

export const fetchParentChildRelationships = async (): Promise<ParentChildRelationship[]> => {
  try {
    const response = await client.get('/parent-child-relationships/');
    
    return response.data;
  } catch (error) {
    console.error('Error fetching parent-child relationships:', error);
    return [];
  }
}

export const updateParentChildRelationship = async (id: string, data: any): Promise<any> => {
  try {
    const response = await client.put(`/parent-child-relationships/${id}/`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createParentChildRelationship = async (data: any): Promise<any> => {
  try {
    const response = await client.post('/parent-child-relationships/', data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchMedicalHistories = async (): Promise<MedicalHistory[]> => {
  const response = await client.get('/medical-histories/')
  return response.data
}

export const fetchMedicalData = async (): Promise<MedicalData> => {
  try {
    const response = await client.get('/medical-data/')
    return response.data
  } catch (error) {
    console.error('Error fetching medical data:', error)
    // Return default medical data with blood types if API call fails
    return {
      blood_types: ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'],
      missing_vaccines: [],
      known_diseases: [],
      allergies: [],
      psychological_disorders: [],
      development_disorders: [],
      underlying_diseases: []
    }
  }
}

export const createMedicalHistory = async (data: Omit<MedicalHistory, 'id' | 'created_at' | 'updated_at'>): Promise<MedicalHistory> => {
  const response = await client.post('/medical-histories/', data)
  return response.data
}

export const deleteMedicalHistory = async (id: number): Promise<void> => {
  await client.delete(`/medical-histories/${id}/`)
}

export const fetchBookings = async (): Promise<Booking[]> => {
  const response = await client.get('/bookings/')
  return response.data
}

export const createBooking = async (data: Omit<Booking, 'id' | 'created_at' | 'updated_at' | 'code' | 'masked_friends' | 'friends'>): Promise<Booking> => {
  const response = await client.post('/bookings/', data)
  return response.data
}

export const updateBooking = async (id: number, data: Partial<Omit<Booking, 'id' | 'created_at' | 'updated_at' | 'code' | 'masked_friends' | 'friends'>>): Promise<Booking> => {
  const response = await client.patch(`/bookings/${id}/`, data)
  return response.data
}

export const deleteBooking = async (id: number): Promise<void> => {
  await client.delete(`/bookings/${id}/`)
}

export const fetchMedicalHistory = async (id: number): Promise<MedicalHistory> => {
  const response = await client.get(`/medical-histories/${id}/`)
  return response.data
}

export const getMedicalHistoryPdfUrl = async (id: number): Promise<string> => {
  try {
    const response = await client.get(`/medical-histories/${id}/download/`);
    
    // Check if the response contains a url property
    if (response.data && response.data.url) {
      return response.data.url;
    } else if (response.data && response.data.status === 'generating') {
      // Handle the case where the PDF is still generating
      throw new Error('PDF is still being generated. Please try again in a few seconds.');
    } else {
      // If no URL is found, throw an error
      throw new Error('Invalid response format. URL not found in response.');
    }
  } catch (error) {
    console.error('Error getting PDF URL:', error);
    throw error;
  }
}

// =============================================================================
// Pricing and Payment Types
// =============================================================================

export interface EventPricing {
  id: number
  event: number
  event_name: string
  year: number
  early_bird_start_date: string | null
  early_bird_end_date: string | null
  is_early_bird_active: boolean
  // Regular prices
  regular_price_1_child: string
  regular_advance_1_child: string
  regular_repayment_1_child: string
  regular_price_2_children: string
  regular_advance_2_children: string
  regular_repayment_2_children: string
  regular_price_3_children: string
  regular_advance_3_children: string
  regular_repayment_3_children: string
  // Early bird prices
  early_bird_price_1_child: string | null
  early_bird_price_2_children: string | null
  early_bird_price_3_children: string | null
}

export interface PriceBreakdown {
  event_id: number
  event_name: string
  num_children: number
  is_early_bird: boolean
  total_cost: string
  advance_payment: string
  repayment: string
  price_per_child: string
  advance_per_child: string
  regular_price_per_child: string
  early_bird_price_per_child: string | null
  booking_ids: number[]
  tier: string
  error: string | null
}

export interface BookingPaymentItem {
  booking_id: number
  payment_type: PaymentType
}

export interface BookingPaymentDetail {
  booking_id: number
  payment_type: PaymentType
  amount: string
  is_early_bird: boolean
}

export interface PaymentSummary {
  breakdowns: PriceBreakdown[]
  total_price: string
  total_advance: string
  total_repayment: string
  amount_due: string
  booking_payment_details: BookingPaymentDetail[]
  all_booking_ids: number[]
  has_errors: boolean
  error_messages: string[]
}

export type PaymentStatus = 'pending' | 'processing' | 'success' | 'failed' | 'cancelled' | 'refunded'
export type PaymentType = 'advance' | 'repayment' | 'full' | 'reserve_spot'

export interface PaymentBookingInfo {
  id: number
  code: string
  child_name: string
  event_name: string
  payment_type: PaymentType
}

export interface Payment {
  id: number
  merchant_reference: string
  parent: number
  parent_name: string
  payment_types: PaymentType[]
  amount: string
  status: PaymentStatus
  description: string
  bookings_info: PaymentBookingInfo[]
  transaction_id: string
  approval_code: string
  card_type: number | null
  card_type_display: string | null
  transaction_datetime: string | null
  created_at: string
  completed_at: string | null
  error_message: string
}

export type PaymentMethodType = 'card' | 'bank_transfer'

export interface PaymentListItem {
  id: number
  merchant_reference: string
  payment_types: PaymentType[]
  payment_method_type: PaymentMethodType
  amount: string
  status: PaymentStatus
  bookings_info: PaymentBookingInfo[]
  created_at: string
  completed_at: string | null
}

export interface InitiatePaymentResponse {
  payment_id: number
  merchant_reference: string
  amount: string
  form_action: string
  form_fields: Record<string, string>
  description: string
}

// =============================================================================
// Pricing and Payment API Functions
// =============================================================================

export const fetchEventPricing = async (eventId: number): Promise<EventPricing> => {
  const response = await client.get(`/events/${eventId}/pricing/`)
  return response.data
}

export const calculatePrice = async (
  bookings: BookingPaymentItem[]
): Promise<PaymentSummary> => {
  const response = await client.post('/payments/calculate-price/', {
    bookings,
  })
  return response.data
}

export const initiatePayment = async (
  bookings: BookingPaymentItem[],
  languageCode: string = 'el-GR'
): Promise<InitiatePaymentResponse> => {
  const response = await client.post('/payments/initiate/', {
    bookings,
    language_code: languageCode,
  })
  return response.data
}

export const fetchPayments = async (): Promise<PaymentListItem[]> => {
  const response = await client.get('/payments/')
  return response.data
}

export const fetchPayment = async (id: number): Promise<Payment> => {
  const response = await client.get(`/payments/${id}/`)
  return response.data
} 